// Product Details Admin Section - Admin interface for managing individual product details
// Follows the Section, Component & Manager System architecture

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { MainLayout } from '../../../../components/Layout';
import { ProductDetailsManager } from '../../subpages/details/managers/product-details-manager';
import { useManager } from '../../../../core';
import { useAuth } from '../../../../contexts/AuthContext';
import { ProductDetailsLayout } from '../../subpages/details/components/ProductDetailsLayout';
import { ProductSearchBar } from '../../components/ProductSearchBar';
import { ProductTagsModal } from '../../subpages/details/components/ProductTagsModal';
import { ProductAccessTagsModal } from '../../subpages/details/components/ProductAccessTagsModal';
import { ProductImageModal } from '../../subpages/details/components/ProductImageModal';
import { ProductDownloadsModal } from '../../subpages/details/components/ProductDownloadsModal';
import { ProductDeleteConfirmModal } from '../../subpages/details/components/ProductDeleteConfirmModal';
import '../../subpages/details/ProductDetailsSection.css';
import './ProductsAdmin.css';

export function ProductDetailsAdminSection() {
  const { user } = useAuth();
  const { manager, isInitialized } = useManager(ProductDetailsManager);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // React state for product data
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isTagsModalOpen, setIsTagsModalOpen] = useState(false);
  const [isAccessTagsModalOpen, setIsAccessTagsModalOpen] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [isDownloadsModalOpen, setIsDownloadsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Search state for details search bar
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const latestSearchRequestRef = useRef(0);
  const activeProductIdRef = useRef('');
  const latestSearchTermRef = useRef('');

  // Get productId from URL parameters
  const productId = searchParams.get('productId') || '';

  // Perform real search API call
  const performSearch = useCallback(async (query, currentProductId) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    const requestId = ++latestSearchRequestRef.current;

    try {
      const response = await manager.searchProductsFromAPI(query, 1, 7); // Limit to 7 results
      let results = response.products || [];

      // Filter out the current product we're viewing (don't show it in search results)
      results = results.filter(product => product.id !== currentProductId);

      // Ignore stale responses from previous requests/transitions
      if (requestId !== latestSearchRequestRef.current) return;
      if (activeProductIdRef.current !== currentProductId) return;
      if (latestSearchTermRef.current !== query) return;

      setSearchResults(results);
    } catch (error) {
      if (requestId !== latestSearchRequestRef.current) return;
      console.error('Search failed:', error);
      setSearchResults([]);
    }
  }, [manager]);

  // Handle search input changes (immediate UI update)
  const handleSearchChange = useCallback((newTerm) => {
    setSearchTerm(newTerm);
    // Debounce and dropdown display is handled by ProductSearchBar component
  }, []);

  // Perform search when searchTerm changes (debounced by ProductSearchBar)
  useEffect(() => {
    if (!isInitialized || !productId || loading) return;

    const trimmedTerm = searchTerm.trim();
    if (!trimmedTerm) {
      latestSearchTermRef.current = '';
      latestSearchRequestRef.current += 1;
      setSearchResults([]);
      return;
    }

    latestSearchTermRef.current = trimmedTerm;
    performSearch(trimmedTerm, productId);
  }, [isInitialized, productId, loading, searchTerm, performSearch]);

  // Handle hard search (Enter key - navigate to main products page)
  const handleSearchSubmit = useCallback((term) => {
    if (term.trim()) {
      // Navigate to main products admin page with search query
      const params = new URLSearchParams();
      params.set('query', term.trim());
      window.location.href = `/marketplace/admin?${params.toString()}`;
    }
  }, []);

  // Handle navigation to main products admin page
  const handleProductsClick = useCallback((e) => {
    e.preventDefault();
    navigate('/marketplace/admin');
  }, [navigate]);

  // Handle tags modal opening
  const handleOpenTagsModal = useCallback(() => {
    setIsTagsModalOpen(true);
  }, []);

  // Handle access tags modal opening
  const handleOpenAccessTagsModal = useCallback(() => {
    setIsAccessTagsModalOpen(true);
  }, []);

  // Build full update payload (include bundleIds and relatedProductIds in all updates)
  const buildUpdateData = useCallback((overrides = {}) => {
    const base = {
      name: product.name || '',
      description: product.description || '',
      price: product.price || 0,
      tags: Array.isArray(product.tags) ? product.tags : [],
      accountTags: Array.isArray(product.accountTags) ? product.accountTags : [],
      thumbnail: typeof product.thumbnail === 'string'
        ? product.thumbnail
        : (typeof product.image === 'string' ? product.image : null),
      images: Array.isArray(product.images) ? product.images : [],
      downloads: Array.isArray(product.downloads) ? product.downloads : [],
      isFeatured: Boolean(product.isFeatured),
      isActive: product.isActive !== false,
      isBundle: Boolean(product.isBundle),
      bundleIds: Array.isArray(product.bundleIds) ? product.bundleIds : [],
      relatedProductIds: Array.isArray(product.relatedProductIds) ? product.relatedProductIds : [],
    };
    return { ...base, ...overrides };
  }, [product]);

  // Handle tags update
  const handleUpdateTags = useCallback(async (newTags) => {
    if (!product) return;

    try {
      const updateData = buildUpdateData({ tags: newTags });
      const updatedProduct = await manager.updateProduct(productId, updateData);
      setProduct(updatedProduct);
    } catch (error) {
      console.error('Failed to update tags:', error);
      throw error; // Let modal handle error display
    }
  }, [product, productId, manager, buildUpdateData]);

  // Handle access tags update
  const handleUpdateAccessTags = useCallback(async (newAccountTags) => {
    if (!product) return;

    try {
      const updateData = buildUpdateData({ accountTags: newAccountTags });
      const updatedProduct = await manager.updateProduct(productId, updateData);
      setProduct(updatedProduct);
    } catch (error) {
      console.error('Failed to update access tags:', error);
      throw error;
    }
  }, [product, productId, manager, buildUpdateData]);

  // Handle image modal opening
  const handleOpenImageModal = useCallback(() => {
    setIsImageModalOpen(true);
  }, []);

  // Handle image update
  const handleUpdateImages = useCallback(async (thumbnail, images) => {
    if (!product) return;

    try {
      const updateData = buildUpdateData({ thumbnail: thumbnail.trim() || null, images: Array.isArray(images) ? images : [] });
      const updatedProduct = await manager.updateProduct(productId, updateData);
      setProduct(updatedProduct);
    } catch (error) {
      console.error('Failed to update images:', error);
      throw error; // Let modal handle error display
    }
  }, [product, productId, manager, buildUpdateData]);

  // Handle downloads modal opening
  const handleOpenDownloadsModal = useCallback(() => {
    setIsDownloadsModalOpen(true);
  }, []);

  // Handle downloads update
  const handleUpdateDownloads = useCallback(async (newDownloads) => {
    if (!product) return;

    try {
      const updateData = buildUpdateData({ downloads: Array.isArray(newDownloads) ? newDownloads : [] });
      const updatedProduct = await manager.updateProduct(productId, updateData);
      setProduct(updatedProduct);
    } catch (error) {
      console.error('Failed to update downloads:', error);
      throw error;
    }
  }, [product, productId, manager, buildUpdateData]);

  // Handle featured toggle
  const handleFeaturedChange = useCallback(async (isFeatured) => {
    if (!product) return;

    try {
      const updateData = buildUpdateData({ isFeatured: Boolean(isFeatured) });
      const updatedProduct = await manager.updateProduct(productId, updateData);
      setProduct(updatedProduct);
    } catch (error) {
      console.error('Failed to update featured status:', error);
    }
  }, [product, productId, manager, buildUpdateData]);

  // Handle bundle toggle
  const handleBundleChange = useCallback(async (isBundle) => {
    if (!product) return;

    try {
      const updateData = buildUpdateData({ isBundle: Boolean(isBundle) });
      const updatedProduct = await manager.updateProduct(productId, updateData);
      setProduct(updatedProduct);
    } catch (error) {
      console.error('Failed to update bundle status:', error);
    }
  }, [product, productId, manager, buildUpdateData]);

  // Handle active toggle
  const handleActiveChange = useCallback(async (isActive) => {
    if (!product) return;

    try {
      const updateData = buildUpdateData({ isActive: Boolean(isActive) });
      const updatedProduct = await manager.updateProduct(productId, updateData);
      setProduct(updatedProduct);
    } catch (error) {
      console.error('Failed to update active status:', error);
    }
  }, [product, productId, manager, buildUpdateData]);

  // Handle product click - navigate to that product's details
  const handleProductClick = useCallback((p) => {
    if (p?.id) navigate(`/marketplace/admin/details?productId=${p.id}`);
  }, [navigate]);

  // Handle add bundle
  const handleAddBundle = useCallback(async (p) => {
    if (!product || !p?.id) return;
    const ids = Array.isArray(product.bundleIds) ? [...product.bundleIds] : [];
    if (ids.some((id) => String(id) === String(p.id))) return;
    ids.push(p.id);
    try {
      const updateData = buildUpdateData({ bundleIds: ids });
      await manager.updateProduct(productId, updateData);
      const refreshed = await manager.getProductById(productId);
      setProduct(refreshed);
    } catch (error) {
      console.error('Failed to add bundle:', error);
    }
  }, [product, productId, manager, buildUpdateData]);

  // Handle remove bundle
  const handleRemoveBundle = useCallback(async (p) => {
    if (!product || !p?.id) return;
    const ids = Array.isArray(product.bundleIds) ? product.bundleIds.filter((id) => String(id) !== String(p.id)) : [];
    try {
      const updateData = buildUpdateData({ bundleIds: ids });
      await manager.updateProduct(productId, updateData);
      const refreshed = await manager.getProductById(productId);
      setProduct(refreshed);
    } catch (error) {
      console.error('Failed to remove bundle:', error);
    }
  }, [product, productId, manager, buildUpdateData]);

  // Handle add related product
  const handleAddRelated = useCallback(async (p) => {
    if (!product || !p?.id) return;
    const ids = Array.isArray(product.relatedProductIds) ? [...product.relatedProductIds] : [];
    if (ids.some((id) => String(id) === String(p.id))) return;
    ids.push(p.id);
    try {
      const updateData = buildUpdateData({ relatedProductIds: ids });
      await manager.updateProduct(productId, updateData);
      const refreshed = await manager.getProductById(productId);
      setProduct(refreshed);
    } catch (error) {
      console.error('Failed to add related product:', error);
    }
  }, [product, productId, manager, buildUpdateData]);

  // Handle remove related product
  const handleRemoveRelated = useCallback(async (p) => {
    if (!product || !p?.id) return;
    const ids = Array.isArray(product.relatedProductIds) ? product.relatedProductIds.filter((id) => String(id) !== String(p.id)) : [];
    try {
      const updateData = buildUpdateData({ relatedProductIds: ids });
      await manager.updateProduct(productId, updateData);
      const refreshed = await manager.getProductById(productId);
      setProduct(refreshed);
    } catch (error) {
      console.error('Failed to remove related product:', error);
    }
  }, [product, productId, manager, buildUpdateData]);

  // Handle delete button click - open confirmation modal
  const handleDeleteClick = useCallback(() => {
    setIsDeleteModalOpen(true);
  }, []);

  // Handle confirm delete
  const handleConfirmDelete = useCallback(async () => {
    if (!productId) return;

    setIsDeleting(true);
    try {
      await manager.deleteProduct(productId);
      setIsDeleteModalOpen(false);
      navigate('/marketplace/admin');
    } catch (error) {
      console.error('Failed to delete product:', error);
    } finally {
      setIsDeleting(false);
    }
  }, [productId, manager, navigate]);

  // Memoize loadProductData to prevent unnecessary re-renders
  const loadProductData = useCallback(async (id) => {
    if (!id) return;

    setLoading(true);
    setError(null);
    try {
      const productData = await manager.getProductById(id);
      setProduct(productData);
    } catch (error) {
      console.error('Failed to load product:', error);
      setError(error.message || 'Failed to load product');
      setProduct(null);
    } finally {
      setLoading(false);
    }
  }, [manager]);

  // Load product when productId changes
  useEffect(() => {
    activeProductIdRef.current = productId || '';
    latestSearchTermRef.current = '';
    latestSearchRequestRef.current += 1;

    if (isInitialized && productId) {
      // Clear search state immediately when navigating to prevent race conditions
      setSearchTerm('');
      setSearchResults([]);
      loadProductData(productId);
    } else {
      setProduct(null);
      setLoading(false);
      setError(null);
      setSearchTerm('');
      setSearchResults([]);
    }
  }, [isInitialized, productId, loadProductData]);

  // Check admin access - only show admin panel for admins
  if (!user || user.role !== 'admin') {
    return (
      <MainLayout contentClassName="main-content--product-details">
        <div className="product-details-section">
          <div className="access-denied">
            <i className="fa-solid fa-shield-alt" style={{ fontSize: '3rem', color: '#ff4444', marginBottom: '20px' }}></i>
            <h1>Access Denied</h1>
            <p>This admin section is only available to administrators.</p>
            <p>Please contact support if you believe this is an error.</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout contentClassName="main-content--product-details">
      <div className="product-details-section">
        {/* Search Bar Row */}
        <div className="product-details-search-row">
          <div className="product-details-controls-row">
            <button
              className="products-admin-btn products-back-btn"
              onClick={() => navigate(`/marketplace/details?productId=${productId}`)}
              title="Back to Details"
            >
              <i className="fa-solid fa-arrow-left"></i>
              Back to Details
            </button>
            <a
              href="/marketplace/admin"
              className="product-details-title-inline"
              onClick={handleProductsClick}
            >
              Marketplace Admin
            </a>
            <ProductSearchBar
              searchTerm={searchTerm}
              onSearchChange={handleSearchChange}
              onSubmit={handleSearchSubmit}
              placeholder="Search products..."
              context="details"
              searchResults={searchResults}
            />
          </div>
        </div>

        {/* Product Details Content Container - max-width 1400px */}
        <div className="product-details-content">
          {loading ? (
            <div className="product-details-loading-state">
              <div className="product-details-loading-content">
                <i className="fa-solid fa-spinner fa-spin"></i>
                <span>Loading product...</span>
              </div>
            </div>
          ) : error ? (
            <div className="product-details-error-state">
              <div className="product-details-error-content">
                <i className="fa-solid fa-exclamation-triangle"></i>
                <h3>Failed to load product</h3>
                <p>{error}</p>
                <p><small>Product ID: {productId}</small></p>
              </div>
            </div>
          ) : product ? (
            <ProductDetailsLayout
              product={product}
              admin={true}
              manager={manager}
              onOpenTagsModal={handleOpenTagsModal}
              onOpenAccessTagsModal={handleOpenAccessTagsModal}
              onOpenImageModal={handleOpenImageModal}
              onOpenDownloadsModal={handleOpenDownloadsModal}
              onFeaturedChange={handleFeaturedChange}
              onActiveChange={handleActiveChange}
              onBundleChange={handleBundleChange}
              onDeleteClick={handleDeleteClick}
              onProductClick={handleProductClick}
              onAddBundle={handleAddBundle}
              onRemoveBundle={handleRemoveBundle}
              onAddRelated={handleAddRelated}
              onRemoveRelated={handleRemoveRelated}
            />
          ) : (
            <div className="product-details-error-state">
              <div className="product-details-error-content">
                <i className="fa-solid fa-exclamation-triangle"></i>
                <h3>Product not found</h3>
                <p>The requested product could not be found.</p>
                <p><small>Product ID: {productId}</small></p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tags Editing Modal */}
      <ProductTagsModal
        isOpen={isTagsModalOpen}
        onClose={() => setIsTagsModalOpen(false)}
        currentTags={product?.tags || []}
        onUpdateTags={handleUpdateTags}
      />

      {/* Access Tags Editing Modal */}
      <ProductAccessTagsModal
        isOpen={isAccessTagsModalOpen}
        onClose={() => setIsAccessTagsModalOpen(false)}
        currentTags={product?.accountTags || []}
        onUpdateTags={handleUpdateAccessTags}
      />

      {/* Image Editing Modal */}
      <ProductImageModal
        isOpen={isImageModalOpen}
        onClose={() => setIsImageModalOpen(false)}
        product={product}
        currentThumbnail={product?.thumbnail || product?.image || ''}
        currentImages={product?.images || []}
        onUpdateImages={handleUpdateImages}
      />

      {/* Downloads Editing Modal */}
      <ProductDownloadsModal
        isOpen={isDownloadsModalOpen}
        onClose={() => setIsDownloadsModalOpen(false)}
        currentDownloads={product?.downloads || []}
        onUpdateDownloads={handleUpdateDownloads}
      />

      {/* Delete Confirmation Modal */}
      <ProductDeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        product={product}
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
      />
    </MainLayout>
  );
}